import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, SeverityDot, Empty } from "../components/ui-helpers";
import { RequirementHover } from "../components/RequirementHover";
import { ArrowRight, PlusCircle, Wrench, ShieldCheck } from "@phosphor-icons/react";

export default function MyAssignments() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("sme");
  const nav = useNavigate();
  const { canEdit } = useAuth();

  const load = useCallback(() => api.get("/my-assignments").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const createTask = async (item) => {
    const gap = item.gaps[0];
    try {
      await api.post("/tasks", {
        title: `Evidence: ${item.standard_id} ${item.req_number}`,
        description: gap?.prompt || gap?.message || "Resolve evidence gap", assignee: data.user_name || "",
        priority: gap?.severity === "high" ? "high" : "medium",
        task_type: "evidence_request", related_type: "requirement", related_id: item.requirement_id,
      });
      toast.success("Task created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-8 font-mono text-sm">Loading…</div>;
  const c = data.counts;
  const items = tab === "sme" ? data.sme : data.compliance;

  return (
    <div>
      <PageHeader title="My Assignments" subtitle={`Signed in as ${data.user_name || "—"} — your work as SME and your Compliance verification queue`} />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border" data-testid="my-assignment-counts">
          <Stat label="SME — action needed" value={c.sme_with_gaps} accent="text-red-600" />
          <Stat label="SME — total assigned" value={c.sme_total} />
          <Stat label="Compliance — ready to verify" value={c.compliance_ready} accent="text-emerald-600" />
          <Stat label="Compliance — pending SME" value={c.compliance_pending} accent="text-amber-600" />
        </div>

        <div className="flex items-center gap-1">
          <button data-testid="tab-sme" onClick={() => setTab("sme")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border ${tab === "sme" ? "bg-primary text-primary-foreground border-black" : "border-border hover:bg-secondary"}`}>
            <Wrench size={15} /> My SME Work ({data.sme.length})
          </button>
          <button data-testid="tab-compliance" onClick={() => setTab("compliance")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border ${tab === "compliance" ? "bg-primary text-primary-foreground border-black" : "border-border hover:bg-secondary"}`}>
            <ShieldCheck size={15} /> My Compliance Queue ({data.compliance.length})
          </button>
        </div>

        {items.length === 0 ? (
          <Empty title={tab === "sme" ? "No SME assignments" : "No verification assignments"}
            hint="Ask an admin to assign your name (Bulk assign roles on Missing Evidence) to start seeing your work here." />
        ) : items.map((item) => (
          <div key={`${item.requirement_id}-${item.my_role}`} data-testid={`my-item-${item.req_number}`} className="border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-5 py-3 bg-secondary/40 gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <RequirementHover label={`${item.standard_id} ${item.req_number}`} text={item.text}>
                  <span className="font-mono font-bold text-sm bg-primary text-primary-foreground px-2 py-0.5 cursor-help">{item.standard_id} {item.req_number}</span>
                </RequirementHover>
                <span className="text-[11px] font-bold uppercase px-1.5 py-0.5 bg-secondary border border-border">{item.my_role}</span>
                <span className="text-sm text-muted-foreground max-w-md truncate">{item.text}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {tab === "sme" ? (
                  item.gap_count > 0
                    ? <span className="text-xs font-semibold text-red-600">{item.gap_count} gap{item.gap_count > 1 ? "s" : ""} to resolve</span>
                    : <span className="text-xs font-semibold text-emerald-600">Up to date</span>
                ) : (
                  item.gap_count > 0
                    ? <span className="text-xs font-semibold text-amber-600">Pending SME action ({item.gap_count})</span>
                    : <span className="text-xs font-semibold text-emerald-600">Ready to verify</span>
                )}
                <button data-testid={`my-open-${item.req_number}`} onClick={() => nav(`/requirements/${item.requirement_id}`)} className="flex items-center gap-1 text-sm font-semibold hover:text-blue-600">Open <ArrowRight size={14} /></button>
              </div>
            </div>
            {item.gaps.length > 0 && (
              <div className="divide-y divide-border">
                {item.gaps.map((g, i) => (
                  <div key={`${item.requirement_id}-${g.type}-${i}`} className="flex items-start justify-between gap-4 px-5 py-3">
                    <div className="flex items-start gap-3">
                      <SeverityDot severity={g.severity} />
                      <div>
                        <span className="text-sm">{g.message}</span>
                        {g.prompt && <p className="text-xs text-muted-foreground mt-0.5">→ {g.prompt}</p>}
                      </div>
                    </div>
                    {canEdit && tab === "sme" && i === 0 && (
                      <button data-testid={`my-create-task-${item.req_number}`} onClick={() => createTask(item)} className="flex items-center gap-1 border border-border px-2 py-1 text-xs font-semibold hover:bg-primary hover:text-white shrink-0"><PlusCircle size={14} />Create task</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-card p-5">
      <div className="overline text-muted-foreground">{label}</div>
      <div className={`font-display font-black text-4xl tracking-tighter mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}
